/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { useMemo } from 'react'

export const useBookingSellerDuplicateOfferSchema = () => {
    const { t } = useTranslation()

    const dateTimeOrString = z.union([z.string(), z.date()]).refine(
        (val) => {
            if (val instanceof Date) {
                return !Number.isNaN(val.getTime())
            }
            return String(val).trim().length > 0
        },
        {
            message: t('Date and time is required'),
        }
    )

    return useMemo(
        () =>
            z.object({
                option: z
                    .union([z.string(), z.number()])
                    .optional()
                    .refine((val) => val !== undefined && String(val).trim().length > 0, {
                        message: t('Select the number of copies: 1x, 2x or 3x.'),
                    }),
                datetime: dateTimeOrString,
                company_id: z.number().optional().nullable(),
                package_category_name: z.string().optional().nullable(),
                calculations: z.array(z.number()),
                lead_id: z.number().optional().nullable(),
                address_id: z.number().optional().nullable(),
                contact_id: z.number().optional().nullable(),
            }),
        [dateTimeOrString, t]
    )
}

export type BookingSellerDuplicateOfferInputs = z.infer<ReturnType<typeof useBookingSellerDuplicateOfferSchema>>
